import type { ConversationLock } from '@repletoryreach/sdk-api/inbox';
import { Group, Button, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { shortId } from '../../../lib/format';

export interface LockIndicatorProps {
  lock: ConversationLock | null | undefined;
  onRelease: () => void;
  releasing?: boolean;
}

/**
 * W9: banner for the conversation edit lock. Someone else's lock: warning
 * with the holder and expiry (the composer is disabled by the pane). Own
 * lock: quiet confirmation with a Release button.
 */
export function LockIndicator({ lock, onRelease, releasing }: LockIndicatorProps) {
  const t = useT();
  if (!lock) return null;

  const until = new Date(lock.expires_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  if (lock.is_mine) {
    return (
      <Alert color="info" className="rr-inbox2__lock rr-inbox2__lock--mine">
        <Group justify="space-between" align="center" wrap="wrap">
          <Text role="bodySmall">
            {t('inbox.lock.mine')} · {t('inbox.lock.until')} {until}
          </Text>
          <Button size="xs" variant="default" loading={releasing} onClick={onRelease}>
            {t('inbox.lock.release')}
          </Button>
        </Group>
      </Alert>
    );
  }

  return (
    <Alert color="warning" title={`${t('inbox.lock.heldBy')} ${lock.holder_name ?? shortId(lock.holder_user_id)}`} className="rr-inbox2__lock rr-inbox2__lock--other">
      <Text role="bodySmall">
        {t('inbox.lock.readOnly')} · {t('inbox.lock.until')} {until}
      </Text>
    </Alert>
  );
}
